import React, { FC, useMemo } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Select from "@/components/Select/Select";
import { useAppTheme } from "@/utils/theme";
import { useRewardsEngine } from "@/hooks/useRewardsEngine";

export type Filters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string;
  rewardConfigurationId: string;
};

type Props = {
  filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
};

const GoalTemplateFilters: FC<Props> = ({ filtersState }) => {
  const theme = useAppTheme();
  const [filters, setFilters] = filtersState;

  const { data: rewardConfigs, isLoading: isLoadingRewardConfigs } =
    useRewardsEngine(true);

  const rewardConfigOptions = useMemo(
    () =>
      (rewardConfigs || []).map((rc) => ({
        key: rc._id,
        value: rc.event
          ? `${rc.event}${rc.eventType ? ` (${rc.eventType})` : ""}`
          : rc._id,
      })),
    [rewardConfigs]
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5rem",
        width: "100%",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "row",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.surface.light2,
          px: 2,
          width: {
            xs: "100%",
            sm: "100%",
          },
        }}
        className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
      >
        <InputBase
          className="flex-1"
          placeholder="Search goal template"
          inputProps={{ "aria-label": "search" }}
          onChange={(e) => {
            setFilters((filters) => ({
              ...filters,
              search: e.target.value,
              pageNumber: 0,
            }));
          }}
          value={filters.search}
        />
        <SearchIcon className="text-icon-primary" />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          width: {
            xs: "100%",
            sm: "100%",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
            md: "row",
          },
        }}
      >
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              status: value as string,
              pageNumber: 0,
            }));
          }}
          data={[
            { key: "ACTIVE", value: "Active" },
            { key: "INACTIVE", value: "Inactive" },
          ]}
          value={filters.status}
          label="Status"
          id="template-status-filter"
          maxWidth={200}
          showClearOption
          fullWidth
        />
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              rewardConfigurationId: value as string,
              pageNumber: 0,
            }));
          }}
          data={rewardConfigOptions}
          value={filters.rewardConfigurationId}
          label="Reward config"
          id="template-reward-config-filter"
          maxWidth={260}
          isLoading={isLoadingRewardConfigs}
          showClearOption
          fullWidth
        />
      </Box>
    </Box>
  );
};

export default GoalTemplateFilters;
